/*
 * Universal Construct — TBI Bank installment payment
 * Shared logic for cart and product contexts.
 *
 * Architecture (2026-06-10 v2):
 *   - Snippet renders <button> + <script type="application/json"> data (no inline JS).
 *   - This file runs once at page load, picks up all TBI buttons + their JSON
 *     config siblings, initializes them. Then a MutationObserver watches the DOM
 *     for new buttons added by Shopify's Section Rendering API (cart drawer
 *     re-renders) and initializes them too — fixes the cart-drawer bug where
 *     inline <script> tags injected via innerHTML do not execute.
 *
 *   - Customer collection: uses Liquid {{ customer }} if all fields present;
 *     otherwise opens snippets/uc-tbi-customer-form modal. Avoids the Shopify
 *     "new customer accounts" Liquid customer object being null even when logged in.
 */
(function () {
  'use strict';
  window.UC = window.UC || {};

  var API_URL = 'https://api.universalconstruct.ro:9443/api/tbi/installment-payment';
  var STORE_ID = 'universalconstruct_ro';
  var DELIVERY_THRESHOLD = 1000;
  var DELIVERY_FEE = 35;
  var INSTALMENTS = 24;  // valoare default trimisă către backend TBI în payload. Pe pagina TBI utilizatorul își alege perioada finală.
  // Pentru AFIȘAJ ("începând de la X RON/lună") folosim UC.TBI_MAX_MONTHS (60) — vezi updateButtonRate care apelează calculateTBI fără al doilea arg → calculatorul folosește MAX_MONTHS
  var CATEGORY = '2';

  function fmtRON(n) {
    return n.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' RON';
  }

  function hasAllFields(customer) {
    if (!customer) return false;
    var addr = customer.default_address || {};
    return !!(customer.first_name && customer.last_name && customer.email &&
      (customer.phone || addr.phone) && addr.address1 && addr.city && addr.province);
  }

  function buildCustomerPayload(c) {
    var addr = c.default_address || {};
    return {
      fname: c.first_name || '',
      lname: c.last_name || '',
      email: c.email || '',
      phone: c.phone || addr.phone || '',
      billing_address: addr.address1 || '',
      billing_city: addr.city || '',
      billing_county: addr.province || '',
      shipping_address: addr.address1 || '',
      shipping_city: addr.city || '',
      shipping_county: addr.province || '',
      cnp: '',
      promo: 0,
      instalments: String(INSTALMENTS)
    };
  }

  function calculateTotal(price) {
    return price < DELIVERY_THRESHOLD ? price + DELIVERY_FEE : price;
  }

  function buildCartOrder(customer) {
    return fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
      if (!cart.items || cart.items.length === 0) {
        throw new Error('Coșul tău este gol. Te rugăm să adaugi produse înainte de a continua cu plata în rate.');
      }
      var price = cart.total_price / 100;
      var calc = window.UC.calculateTBI(price, INSTALMENTS);
      if (!calc.eligible) throw new Error(eligibilityMessage(calc, price));
      return {
        store_id: STORE_ID,
        order_id: 'shopify-cart-' + cart.token,
        order_total: calculateTotal(price),
        customer_id: customer.id ? String(customer.id) : 'guest-' + Date.now(),
        customer: buildCustomerPayload(customer),
        items: cart.items.map(function (item) {
          return {
            name: item.product_title,
            qty: item.quantity.toFixed(4),
            quantity: item.quantity,             // Required by Shopify Admin API (alongside qty for TBI compat)
            variant_id: item.variant_id,         // Required by Shopify Admin API for line_items
            price: item.final_price / 100,
            category: CATEGORY,
            sku: item.sku || String(item.variant_id),
            ImageLink: item.image ? 'https:' + item.image : ''
          };
        })
      };
    });
  }

  function buildProductOrder(customer, productData) {
    var variant = getSelectedVariant(productData);
    if (!variant || !variant.available) {
      throw new Error('Te rugăm să selectezi o variantă disponibilă.');
    }
    var price = variant.price / 100;
    var calc = window.UC.calculateTBI(price, INSTALMENTS);
    if (!calc.eligible) throw new Error(eligibilityMessage(calc, price));
    return Promise.resolve({
      store_id: STORE_ID,
      order_id: 'shopify-product-' + Date.now(),
      order_total: calculateTotal(price),
      customer_id: customer.id ? String(customer.id) : 'guest-' + Date.now(),
      customer: buildCustomerPayload(customer),
      items: [{
        name: productData.title,
        qty: '1',
        quantity: 1,                              // Required by Shopify Admin API
        variant_id: variant.id,                   // Required by Shopify Admin API for line_items
        price: price,
        category: CATEGORY,
        sku: variant.sku || String(variant.id),
        ImageLink: (variant.featured_image && variant.featured_image.src) || productData.featured_image || ''
      }]
    });
  }

  function eligibilityMessage(calc, price) {
    if (calc.reason === 'below_min') {
      return 'Suma minimă pentru plata în rate este ' + calc.min + ' RON. Valoarea actuală: ' + price.toFixed(2) + ' RON.';
    }
    if (calc.reason === 'above_max') {
      return 'Suma maximă pentru plata în rate online este ' + calc.max + ' RON. Pentru sume mai mari, contactează-ne direct.';
    }
    return 'Această sumă nu este eligibilă pentru plată în rate.';
  }

  /* ──────────── Variant detection (product context) ──────────── */

  /**
   * Find the currently selected variant for a product page.
   * Looks at the hidden input populated by Canopy's variant picker.
   * Falls back to first available variant, then first variant.
   */
  function getSelectedVariant(productData) {
    if (!productData || !productData.variants || !productData.variants.length) return null;
    // The product form's hidden variant input is updated by Canopy on variant change.
    var variantInput = document.querySelector('product-form input[name="id"]')
      || document.querySelector('form[action*="/cart/add"] input[name="id"]')
      || document.querySelector('input[name="id"]');
    var variantId = variantInput && variantInput.value;
    if (variantId) {
      var found = productData.variants.find(function (v) { return String(v.id) === String(variantId); });
      if (found) return found;
    }
    var firstAvailable = productData.variants.find(function (v) { return v.available; });
    return firstAvailable || productData.variants[0];
  }

  function getProductPrice(productData) {
    var variant = getSelectedVariant(productData);
    return variant ? variant.price / 100 : 0;
  }

  /* ──────────── Form modal ──────────── */

  function getForm() { return document.getElementById('uc-tbi-customer-form'); }
  function getProcessingOverlay() { return document.getElementById('uc-tbi-processing'); }

  function showProcessing() {
    var el = getProcessingOverlay();
    if (el) el.removeAttribute('hidden');
  }
  function hideProcessing() {
    var el = getProcessingOverlay();
    if (el) el.setAttribute('hidden', '');
  }

  function setFieldError(input, hasError) {
    if (!input) return;
    if (hasError) input.setAttribute('aria-invalid', 'true');
    else input.removeAttribute('aria-invalid');
  }

  function validateForm(form) {
    var fields = ['first_name', 'last_name', 'email', 'phone', 'address', 'city', 'county'];
    var firstInvalid = null;
    fields.forEach(function (name) {
      var input = form.elements[name];
      var val = input ? (input.value || '').trim() : '';
      var invalid = !val;
      if (!invalid && name === 'email') invalid = !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
      if (!invalid && name === 'phone') invalid = !/^[0-9+()\s-]{8,}$/.test(val);
      setFieldError(input, invalid);
      if (invalid && !firstInvalid) firstInvalid = input;
    });
    return firstInvalid;
  }

  function readForm(form) {
    var get = function (name) { return (form.elements[name].value || '').trim(); };
    return {
      id: null,
      first_name: get('first_name'),
      last_name: get('last_name'),
      email: get('email'),
      phone: get('phone'),
      default_address: {
        address1: get('address'),
        city: get('city'),
        province: get('county'),
        phone: get('phone')
      }
    };
  }

  function prefillForm(form, customer) {
    if (!customer) return;
    var addr = customer.default_address || {};
    var assign = function (name, val) {
      var input = form.elements[name];
      if (input && !input.value && val) input.value = val;
    };
    assign('first_name', customer.first_name);
    assign('last_name', customer.last_name);
    assign('email', customer.email);
    assign('phone', customer.phone || addr.phone);
    assign('address', addr.address1);
    assign('city', addr.city);
    assign('county', addr.province);
  }

  function showFormError(dialog, message) {
    var box = dialog.querySelector('[data-uc-tbi-error]');
    if (!box) return;
    if (message) {
      box.textContent = message;
      box.removeAttribute('hidden');
    } else {
      box.textContent = '';
      box.setAttribute('hidden', '');
    }
  }

  function updateFormRateBox(dialog, calc) {
    var box = dialog.querySelector('[data-uc-tbi-rate]');
    if (!box) return;
    if (!calc || !calc.eligible) {
      box.setAttribute('hidden', '');
      return;
    }
    var label = dialog.querySelector('.uc-tbi-form__rate-label');
    var value = dialog.querySelector('[data-uc-tbi-rate-value]');
    var period = dialog.querySelector('[data-uc-tbi-rate-period]');
    if (label) {
      label.textContent = calc.isVariablePeriod ? 'Plată în rate începând de la' : 'Vei plăti aproximativ';
    }
    if (value) value.textContent = fmtRON(calc.rata);
    if (period) {
      period.textContent = calc.isVariablePeriod ? '/lună' : '/lună timp de ' + calc.perioada + ' luni';
    }
    box.removeAttribute('hidden');
  }

  function openForm(opts) {
    var dialog = getForm();
    if (!dialog) {
      alert('Formularul TBI nu este încărcat. Reîncarcă pagina sau contactează suportul.');
      return Promise.reject(new Error('uc-tbi-customer-form missing'));
    }
    var form = dialog.querySelector('form');
    if (!form) return Promise.reject(new Error('form element missing'));

    prefillForm(form, opts.customer);
    updateFormRateBox(dialog, opts.calc);
    showFormError(dialog, null);

    return new Promise(function (resolve, reject) {
      function cleanup() {
        form.removeEventListener('submit', onSubmit);
        dialog.querySelectorAll('[data-uc-tbi-cancel]').forEach(function (el) {
          el.removeEventListener('click', onCancel);
        });
        dialog.removeEventListener('cancel', onCancel);
      }
      function onSubmit(e) {
        e.preventDefault();
        var firstInvalid = validateForm(form);
        if (firstInvalid) {
          showFormError(dialog, 'Completează toate câmpurile marcate.');
          firstInvalid.focus();
          return;
        }
        showFormError(dialog, null);
        var customer = readForm(form);
        cleanup();
        dialog.close();
        resolve(customer);
      }
      function onCancel(e) {
        if (e) e.preventDefault();
        cleanup();
        if (dialog.open) dialog.close();
        reject(new Error('cancelled'));
      }
      form.addEventListener('submit', onSubmit);
      dialog.querySelectorAll('[data-uc-tbi-cancel]').forEach(function (el) {
        el.addEventListener('click', onCancel);
      });
      dialog.addEventListener('cancel', onCancel);

      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    });
  }

  /* ──────────── Button rate display ──────────── */

  function updateButtonRate(btn, amount) {
    var rateEl = btn.querySelector('.uc-tbi-button__rate');
    var wrapper = btn.closest('.uc-tbi-wrapper');
    // Pentru afișaj: nu pasez perioada, calculatorul folosește MAX_MONTHS=60 (cea mai mică rată) pentru brackete >2000.
    // Pentru bracket-ul ≤2000, fixedPeriod=4 din calculator forțează 4 oricum.
    var calc = window.UC.calculateTBI(amount);
    if (!calc.eligible) {
      if (wrapper) wrapper.style.display = 'none';
      return null;
    }
    if (wrapper) wrapper.style.display = '';
    if (rateEl) {
      if (calc.isVariablePeriod) {
        rateEl.textContent = 'începând de la ' + fmtRON(calc.rata) + '/lună';
      } else {
        rateEl.textContent = 'de la ' + fmtRON(calc.rata) + '/lună × ' + calc.perioada + ' rate';
      }
    }
    return calc;
  }

  /* ──────────── Per-button init ──────────── */

  function initButton(btn) {
    if (!btn || btn.dataset.ucTbiInit === '1') return;
    var wrapper = btn.closest('.uc-tbi-wrapper');
    if (!wrapper) return;
    var dataScript = wrapper.querySelector('script.uc-tbi-config');
    if (!dataScript) return;

    var config;
    try {
      config = JSON.parse(dataScript.textContent);
    } catch (e) {
      console.error('TBI config JSON parse error:', e);
      return;
    }

    btn.dataset.ucTbiInit = '1';

    // For product context, the displayed variant might differ from the Liquid-rendered
    // initialAmount. Always recompute from the currently selected variant.
    var initialAmount = (config.context === 'product' && config.productData)
      ? getProductPrice(config.productData)
      : config.initialAmount;

    updateButtonRate(btn, initialAmount);

    if (config.context === 'product' && config.productData) {
      var refresh = function () {
        updateButtonRate(btn, getProductPrice(config.productData));
      };
      // Canopy fires its own variant event names; listen to multiple to be safe.
      ['variant:change', 'variant:changed', 'on:variant:change'].forEach(function (ev) {
        document.addEventListener(ev, refresh);
      });
      // Fallback: observe the variant input value changes (covers themes that don't fire events).
      var variantInput = document.querySelector('product-form input[name="id"]')
        || document.querySelector('form[action*="/cart/add"] input[name="id"]')
        || document.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.addEventListener('change', refresh);
        // MutationObserver on the input's value attribute (some pickers set via .value, no event)
        var mo = new MutationObserver(refresh);
        mo.observe(variantInput, { attributes: true, attributeFilter: ['value'] });
        // Polling fallback (low-cost, last resort): re-check every 1s while page open
        var lastVal = variantInput.value;
        setInterval(function () {
          if (variantInput.value !== lastVal) {
            lastVal = variantInput.value;
            refresh();
          }
        }, 1000);
      }
    }

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      var titleEl = btn.querySelector('.uc-tbi-button__title');
      var rateEl = btn.querySelector('.uc-tbi-button__rate');
      var originalTitle = titleEl ? titleEl.textContent : btn.textContent;
      var originalRate = rateEl ? rateEl.textContent : '';

      Promise.resolve().then(function () {
        var amount = config.context === 'cart'
          ? config.initialAmount
          : getProductPrice(config.productData);
        // Pentru afișaj în modal: nu pasez perioada (default 60 din calculator pentru brackete >2000)
        var calc = window.UC.calculateTBI(amount);
        if (!calc.eligible) throw new Error(eligibilityMessage(calc, amount));

        if (hasAllFields(config.customer)) return config.customer;
        return openForm({ customer: config.customer, calc: calc });
      }).then(function (customer) {
        if (!customer) return null;
        if (titleEl) titleEl.textContent = 'Se procesează...';
        if (rateEl) rateEl.textContent = '';
        btn.disabled = true;
        showProcessing();

        var orderPromise = config.context === 'cart'
          ? buildCartOrder(customer)
          : buildProductOrder(customer, config.productData);

        return orderPromise.then(function (orderData) {
          return fetch(API_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Cache-Control': 'no-cache' },
            body: JSON.stringify(orderData)
          }).then(function (res) { return res.json(); });
        });
      }).then(function (result) {
        if (!result) return;
        if (result.redirectUrl) {
          // Lăsăm overlay-ul vizibil — redirect-ul preia controlul și pagina se schimbă oricum
          window.location.href = result.redirectUrl;
          return;
        }
        hideProcessing();
        if (result.success) {
          alert('Plata a fost procesată cu succes!');
        } else {
          console.error('TBI Error:', result);
          alert('Eroare la procesarea plății: ' + (result.message || result.error || 'Te rugăm să încerci din nou.'));
        }
      }).catch(function (err) {
        hideProcessing();
        if (err && err.message === 'cancelled') return;
        console.error('TBI button error:', err);
        alert(err && err.message ? err.message : 'A apărut o eroare tehnică. Te rugăm să încerci din nou sau să contactezi suportul.');
      }).then(function () {
        if (titleEl) titleEl.textContent = originalTitle;
        if (rateEl) rateEl.textContent = originalRate;
        btn.disabled = false;
      });
    });
  }

  /* ──────────── Page-level init + Section Rendering API watcher ──────────── */

  function initAllButtons() {
    if (!window.UC || typeof window.UC.calculateTBI !== 'function') {
      // Calculator not loaded yet; retry shortly.
      return setTimeout(initAllButtons, 50);
    }
    document.querySelectorAll('.uc-tbi-button').forEach(initButton);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllButtons);
  } else {
    initAllButtons();
  }

  // Watch for new buttons added to the DOM (Shopify Section Rendering API replaces
  // cart-drawer HTML via innerHTML — inline <script> tags don't execute on insertion,
  // so we re-initialize from here.)
  var observer = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType !== 1) continue;
        if (node.matches && node.matches('.uc-tbi-button')) {
          initButton(node);
          continue;
        }
        if (node.querySelectorAll) {
          node.querySelectorAll('.uc-tbi-button').forEach(initButton);
        }
      }
    }
  });
  observer.observe(document.body || document.documentElement, { childList: true, subtree: true });

  // Public API kept for backward compatibility, in case external code calls it.
  window.UC.initTBI = function (btn, _config) { initButton(btn); };
})();
